import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import subscriptionAccessService from '../../services/subscriptionAccessService';
import { moduleKeyForPath, isModuleActive, MODULE_BY_KEY } from '../../constants/moduleCatalog';
import { resolveTenantId } from '../../utils/tenantIdentity';
import CompanyShatterLoader from '../common/CompanyShatterLoader';

/**
 * Tenant-module guard layered underneath the existing role/permission guard.
 * Existing tenants remain open until Super Admin explicitly enables strict
 * module management. This avoids breaking production companies during rollout.
 */
const ModuleAccessRoute = ({ children }) => {
  const location = useLocation();
  const { userData, isSuperAdmin } = useAuth();
  const tenantId = resolveTenantId(userData);
  const moduleKey = moduleKeyForPath(location.pathname);
  const cachedAccess = React.useMemo(
    () => (moduleKey && tenantId ? subscriptionAccessService.peekTenantModuleAccess(tenantId) : null),
    [moduleKey, tenantId]
  );
  const [state, setState] = React.useState({
    loading: Boolean(moduleKey && tenantId && !cachedAccess),
    access: cachedAccess,
  });

  React.useEffect(() => {
    let active = true;

    if (isSuperAdmin || !moduleKey || !tenantId) {
      setState({ loading: false, access: null });
      return () => { active = false; };
    }

    const cached = subscriptionAccessService.peekTenantModuleAccess(tenantId);
    if (cached) setState({ loading: false, access: cached });
    else setState((current) => ({ ...current, loading: true }));

    subscriptionAccessService
      .getTenantModuleAccess(tenantId)
      .then((access) => {
        if (active) setState({ loading: false, access });
      })
      .catch((error) => {
        console.warn('Tenant module access lookup failed; preserving legacy access:', error);
        if (active) setState({ loading: false, access: { strict: false, legacyOpen: true, activeModules: [] } });
      });

    return () => { active = false; };
  }, [tenantId, moduleKey, isSuperAdmin]);

  if (state.loading) {
    return <CompanyShatterLoader label="Checking module access…" />;
  }

  if (moduleKey && !isModuleActive(state.access, moduleKey)) {
    return (
      <Navigate
        to={`/premium-addons?module=${encodeURIComponent(moduleKey)}`}
        replace
        state={{
          from: location.pathname,
          lockedModule: moduleKey,
          lockedModuleLabel: MODULE_BY_KEY[moduleKey]?.label || moduleKey,
        }}
      />
    );
  }

  return children;
};

export default ModuleAccessRoute;
